'use client';

import { Dispatch, FormEvent, SetStateAction, useState } from 'react';
import { Button, Flex, Modal, Spacing08, WhiteLabelValues } from '@nyshex/keel';
import { GdprContent } from '@/app/shared/components/GdprModals/styled';
import Link from 'next/link';
import { DeclineGdpModal } from '@/app/shared/components/GdprModals/DeclineGdpModal';
import { WhiteLabelPaths } from '@/app/types/types';

const ModalFooter = ({
  handleLogin,
  setDeclineModalOpen,
  whiteLabel,
}: {
  handleLogin: (
    e?: FormEvent<HTMLFormElement>,
    acceptTos?: boolean
  ) => Promise<void>;
  setDeclineModalOpen: Dispatch<SetStateAction<boolean>>;
  whiteLabel: WhiteLabelPaths | undefined;
}) => {
  const [loading, setLoading] = useState(false);

  const handleAgree = async () => {
    setLoading(true);
    try {
      await handleLogin(undefined, true);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setDeclineModalOpen(true);
  };

  return (
    <Flex gap={Spacing08}>
      <Button
        content="Decline"
        variant="secondary"
        size="sm"
        onClick={handleDecline}
        whiteLabel={whiteLabel as unknown as WhiteLabelValues}
        testId="declineButton"
      />
      <Button
        content="Agree To Terms"
        variant="primary"
        size="sm"
        onClick={handleAgree}
        whiteLabel={whiteLabel as unknown as WhiteLabelValues}
        testId="agreeToTermsButton"
        loading={loading}
      />
    </Flex>
  );
};

export const GdprModal = ({
  isOpen,
  setIsOpen,
  handleLogin,
  whiteLabel,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleLogin: (
    e?: FormEvent<HTMLFormElement>,
    acceptTos?: boolean
  ) => Promise<void>;
  whiteLabel?: WhiteLabelPaths;
}) => {
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  return declineModalOpen ? (
    <DeclineGdpModal
      isOpen={declineModalOpen}
      setIsOpen={setDeclineModalOpen}
      setGdprModalOpen={setIsOpen}
      whiteLabel={whiteLabel}
    />
  ) : (
    <Modal
      title="Updated Terms of Service and Privacy Policy"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      footer={
        <ModalFooter
          handleLogin={handleLogin}
          setDeclineModalOpen={setDeclineModalOpen}
          whiteLabel={whiteLabel}
        />
      }
    >
      <GdprContent>
        We prioritize your privacy, which is why we’ve been GDPR compliant from
        the start and only store the data necessary to provide our services. We
        believe privacy is for everyone, so we’re committed to upholding the
        high standards of European data protection laws for all our users,
        regardless of location.
        <br />
        <br />
        The{' '}
        <Link
          passHref
          href="https://www.nyshex.com/terms-of-use"
          target="_blank"
        >
          Platform Terms of Service
        </Link>
        ,{' '}
        <Link
          href="https://nyshex.com/proactive-performance-management-terms-of-service"
          passHref
          target="_blank"
        >
          PPM Terms of Service
        </Link>
        , and{' '}
        <Link
          passHref
          target="_blank"
          href="https://www.nyshex.com/privacy-policy"
        >
          Privacy Policy
        </Link>{' '}
        will take effect immediately on your login date and, by using NYSHEX or
        any of our services on or after that date, you agree to these terms.
      </GdprContent>
    </Modal>
  );
};
